import React, { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../utils/toastConfig';
import api from '../api';
import {
    PageHeader,
    Card,
    Button,
    LoadingSpinner,
    Input,
    Pagination,
    Modal,
    Table // --- Imported Table component ---
} from '../components/UI';
import Select from '../components/Select';
import { useAuth } from '../context/AuthContext';
// Note: EmptyState component was imported but not used, so I've removed it for cleanup.
// If you plan to use it later, you can re-add the import.
// import EmptyState from '../components/EmptyState'; 
import * as I from 'lucide-react';

const VendorSearchPage = () => {
    const navigate = useNavigate();
    const { isAuditor } = useAuth(); // AUDITOR is read-only: hide all mutating actions

    // Add event listener for opening the add vendor modal
    useEffect(() => {
        const handleOpenAddVendor = () => {
            handleOpenAddModal();
        };

        window.addEventListener('openAddVendorModal', handleOpenAddVendor);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('openAddVendorModal', handleOpenAddVendor);
        };
    }, []);

    // State
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [vendorToDelete, setVendorToDelete] = useState(null);

    // Filter state
    const [filter, setFilter] = useState('ALL');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalVendors, setTotalVendors] = useState(0);
    const pageSize = 10;

    // --- TDS Rules State ---
    const [tdsRules, setTdsRules] = useState([]);
    const [loadingTdsRules, setLoadingTdsRules] = useState(false);

    // --- Add Vendor Form State ---
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addFormError, setAddFormError] = useState('');
    const [newVendorData, setNewVendorData] = useState({
        name: '',
        taxId: '',
        panNumber: '',
        tanNumber: '',
        bankAccountNumber: '',
        bankName: '',
        bankIfscCode: '',
        bankBranch: '',
        tdsRuleId: '',
        emails: [] // --- Added emails ---
    });

    // --- Edit Vendor Modal State ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null); // Holds the vendor being edited
    const [isUpdating, setIsUpdating] = useState(false);
    const [editFormError, setEditFormError] = useState('');

    // --- View Vendor Modal State ---
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingVendor, setViewingVendor] = useState(null); // Holds the vendor being viewed

    // --- New TDS Rule Modal State ---
    const [isAddTdsRuleModalOpen, setIsAddTdsRuleModalOpen] = useState(false);
    const [isCreatingTdsRule, setIsCreatingTdsRule] = useState(false);
    const [newTdsRuleData, setNewTdsRuleData] = useState({
        ruleCode: '',
        description: '',
        percentage: ''
    });

    // Check authentication
    const checkAuth = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login', { replace: true });
            return false;
        }
        return true;
    }, [navigate]);

    // Fetch TDS Rules function - matches vendor data fetching pattern
    const fetchTdsRules = useCallback(async () => {
        setLoadingTdsRules(true);
        setError('');

        if (!checkAuth()) {
            console.log('Authentication check failed');
            setLoadingTdsRules(false);
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token found');
            navigate('/login');
            setLoadingTdsRules(false);
            return;
        }

        try {
            console.log('Fetching TDS rules...');
            // Use the pre-configured axios instance which already has the token and CORS settings
            const response = await api.get('/tds-rules');

            console.log('TDS Rules Response:', response.data);
            const rules = Array.isArray(response.data) ? response.data : [];
            console.log('Processed TDS Rules:', rules);
            setTdsRules(rules);
        } catch (err) {
            console.error('Error fetching TDS rules:', err);
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }
            showToast(err.response?.data?.message || 'Failed to fetch TDS rules.', 'error');
            setTdsRules([]);
        } finally {
            setLoadingTdsRules(false);
        }
    }, [checkAuth, navigate]);

    // Open Add Modal Handler
    const handleOpenAddModal = () => {
        setNewVendorData({
            name: '',
            taxId: '',
            panNumber: '',
            tanNumber: '',
            bankAccountNumber: '',
            bankName: '',
            bankIfscCode: '',
            bankBranch: '',
            tdsRuleId: '',
            emails: []
        }); // Reset form
        setAddFormError('');
        setIsAddModalOpen(true);
        fetchTdsRules().catch(console.error);
    };

    // Open Edit Modal Handler
    const handleOpenEditModal = (vendor) => {
        console.log('Editing vendor:', vendor);
        console.log('Vendor TDS rule:', vendor.tdsRule);

        const getTdsRuleId = () => {
            if (vendor.tdsRuleId) return vendor.tdsRuleId.toString();
            if (vendor.tdsRule && vendor.tdsRule.id) return vendor.tdsRule.id.toString();
            return '';
        };

        const tdsRuleId = getTdsRuleId();

        setEditingVendor({
            ...vendor,
            taxId: vendor.taxId || '',
            tdsRuleId: tdsRuleId,
            originalTdsRuleId: tdsRuleId,
            emails: vendor.emails ? vendor.emails.map(e => ({ ...e })) : []
        });

        setEditFormError('');
        setIsEditModalOpen(true);
        fetchTdsRules().catch(error => {
            console.error('Error fetching TDS rules:', error);
            showToast('Failed to load TDS rules. Please try again.', 'error');
        });
    };

    // Handle TDS Rule creation
    const handleCreateTdsRule = async (e) => {
        if (e) e.preventDefault();
        
        if (!newTdsRuleData.ruleCode || !newTdsRuleData.description || !newTdsRuleData.percentage) {
            showToast('Please fill all required TDS rule fields.', 'error');
            return;
        }

        setIsCreatingTdsRule(true);
        try {
            const response = await api.post('/tds-rules', {
                ...newTdsRuleData,
                percentage: parseFloat(newTdsRuleData.percentage)
            });
            const createdRule = response.data;
            showToast('TDS Rule created successfully!', 'success');
            
            // Refresh rules list
            await fetchTdsRules();
            
            // Auto-select the new rule in the active vendor form
            if (isAddModalOpen) {
                setNewVendorData(prev => ({ ...prev, tdsRuleId: createdRule.id.toString() }));
            } else if (isEditModalOpen) {
                setEditingVendor(prev => ({ ...prev, tdsRuleId: createdRule.id.toString() }));
            }
            
            // Reset and close
            setNewTdsRuleData({ ruleCode: '', description: '', percentage: '' });
            setIsAddTdsRuleModalOpen(false);
        } catch (err) {
            console.error('Error creating TDS rule:', err);
            showToast(err.response?.data?.message || 'Failed to create TDS rule.', 'error');
        } finally {
            setIsCreatingTdsRule(false);
        }
    };

    // Open View Modal Handler
    const handleOpenViewModal = (vendor) => {
        console.log('Viewing vendor:', vendor);
        setViewingVendor(vendor);
        setIsViewModalOpen(true);
    };

    // Debounce search input
    const debouncedSearch = useMemo(
        () => debounce((term) => {
            setCurrentPage(0);
            setDebouncedSearchTerm(term);
        }, 300),
        []
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        debouncedSearch(term);
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        setCurrentPage(0);
        setFilter(e.target.value);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage - 1); // Convert 1-based to 0-based for API
    };

    // Fetch vendors from API
    const fetchVendors = useCallback(async () => {
        setLoading(true);
        setError('');

        if (!checkAuth()) {
            console.log('Authentication check failed'); setLoading(false); return;
        }
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token found'); navigate('/login'); setLoading(false); return;
        }

        const params = new URLSearchParams({
            page: currentPage,
            size: pageSize,
            sort: 'name,asc',
            ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
            ...(filter !== 'ALL' && { tdsRuleStatus: filter })
        });
        const config = { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true };

        try {
            const response = await api.get(`/vendors?${params.toString()}`, config);
            const data = response.data;
            setVendors(data.content || []);
            setTotalVendors(data.totalElements || 0);
            setTotalPages(data.totalPages || 0);
        } catch (err) {
            console.error('Error fetching vendors:', err);
            if (err.response?.status === 401) { localStorage.removeItem('token'); navigate('/login'); return; }
            setError(err.response?.data?.message || 'Failed to fetch vendors.');
            setVendors([]); setTotalVendors(0); setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, navigate, checkAuth, filter]);

    // Main data fetch effect
    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    // Generate a new TDS rule ID
    const generateTdsRuleId = () => {
        return 'TDS' + Math.random().toString(36).substr(2, 8).toUpperCase();
    };

    // --- Add Vendor Form Handlers ---
    const handleAddFormChange = (e) => {
        const { name, value } = e.target;
        setNewVendorData(prev => ({ ...prev, [name]: value }));
        if (addFormError) setAddFormError('');
    };

    const handleAddFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setAddFormError('');

        const taxId = newVendorData.taxId?.trim();
        if (!taxId) {
            setAddFormError('Tax ID is required');
            setIsSubmitting(false);
            return;
        }

        if (!newVendorData.tdsRuleId) {
            setAddFormError('Please select a TDS Rule.');
            setIsSubmitting(false);
            return;
        }

        const token = localStorage.getItem('token');
        const config = {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        };

        const tdsRuleId = newVendorData.tdsRuleId || generateTdsRuleId();

        const payload = {
            name: newVendorData.name.trim(),
            taxId: taxId,
            panNumber: newVendorData.panNumber?.trim() || null,
            tanNumber: newVendorData.tanNumber?.trim() || null,
            bankAccountNumber: newVendorData.bankAccountNumber?.trim() || null,
            bankName: newVendorData.bankName?.trim() || null,
            bankIfscCode: newVendorData.bankIfscCode?.trim() || null,
            bankBranch: newVendorData.bankBranch?.trim() || null,
            tdsRuleId: tdsRuleId,
            tdsRule: {
                id: tdsRuleId,
                ruleCode: tdsRuleId,
                description: `TDS Rule for ${newVendorData.name.trim()}`,
                // Add any other required TDS rule fields here
                isActive: true,
                createdAt: new Date().toISOString()
            },
            emails: newVendorData.emails
        };

        console.log('Generated TDS Rule ID:', tdsRuleId);

        console.log('Creating vendor with payload:', payload); // Debug log

        try {
            await api.post('/vendors', payload, config);
            showToast('Vendor created successfully!', 'success');
            setIsAddModalOpen(false);
            // Refresh list (go to first page if not already there)
            if (currentPage !== 0) setCurrentPage(0); else fetchVendors();
        } catch (err) {
            console.error('Error creating vendor:', err);
            const errorMessage = err.response?.data?.message || 'Failed to create vendor.';
            setAddFormError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Edit Vendor Form Handlers ---
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        console.log('Form field changed:', { name, value }); // Debug log

        setEditingVendor(prev => {
            const updated = { ...prev, [name]: value };
            console.log('Updated vendor data:', updated); // Debug log
            return updated;
        });

        if (editFormError) setEditFormError('');
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        setEditFormError('');

        if (!editingVendor) return;

        const taxId = editingVendor.taxId?.trim();
        if (!taxId) {
            setEditFormError('Tax ID is required');
            setIsUpdating(false);
            return;
        }

        if (!editingVendor.tdsRuleId) {
            setEditFormError('Please select a TDS Rule.');
            setIsUpdating(false);
            return;
        }

        const token = localStorage.getItem('token');
        const config = {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        };

        // Check if TDS rule was changed
        const tdsRuleChanged = editingVendor.originalTdsRuleId !== editingVendor.tdsRuleId;

        const payload = {
            name: editingVendor.name.trim(),
            taxId: taxId,
            panNumber: editingVendor.panNumber?.trim() || null,
            tanNumber: editingVendor.tanNumber?.trim() || null,
            bankAccountNumber: editingVendor.bankAccountNumber?.trim() || null,
            bankName: editingVendor.bankName?.trim() || null,
            bankIfscCode: editingVendor.bankIfscCode?.trim() || null,
            bankBranch: editingVendor.bankBranch?.trim() || null,
            tdsRuleId: editingVendor.tdsRuleId || null,
            // Only include tdsRule in payload if it was changed
            ...(tdsRuleChanged && editingVendor.tdsRuleId && {
                tdsRule: {
                    id: editingVendor.tdsRuleId,
                    ruleCode: editingVendor.tdsRuleId,
                    description: `TDS Rule for ${editingVendor.name.trim()}`,
                    isActive: true,
                }
            }),
            emails: editingVendor.emails
        };

        console.log('Updating vendor with payload:', payload);

        try {
            await api.put(`/vendors/${editingVendor.id}`, payload, config);
            showToast('Vendor updated successfully!', 'success');

            setIsEditModalOpen(false);
            setEditingVendor(null);
            fetchVendors(); // Refresh the list
        } catch (err) {
            console.error('Error updating vendor:', err);
            const errorMessage = err.response?.data?.message || 'Failed to update vendor.';
            setEditFormError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    // --- Email Management Handlers ---
    const handleAddEmail = (isEdit = false) => {
        const newEmail = { email: '', default: false, cc: false };
        if (isEdit) {
            setEditingVendor(prev => ({
                ...prev,
                emails: [...(prev.emails || []), newEmail]
            }));
        } else {
            setNewVendorData(prev => ({
                ...prev,
                emails: [...(prev.emails || []), newEmail]
            }));
        }
    };

    const handleRemoveEmail = (index, isEdit = false) => {
        if (isEdit) {
            setEditingVendor(prev => ({
                ...prev,
                emails: prev.emails.filter((_, i) => i !== index)
            }));
        } else {
            setNewVendorData(prev => ({
                ...prev,
                emails: prev.emails.filter((_, i) => i !== index)
            }));
        }
    };

    const handleEmailChange = (index, field, value, isEdit = false) => {
        const updateEmails = (emails) => {
            const updated = [...emails];
            if (field === 'default' && value === true) {
                // If setting as default, uncheck others
                updated.forEach(e => e.default = false);
                // Also ensure it's not CC (optional, but logical)
                updated[index].cc = false;
            }
            if (field === 'cc' && value === true) {
                // If setting as CC, ensure it's not default
                updated[index].default = false;
            }
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        };

        if (isEdit) {
            setEditingVendor(prev => ({
                ...prev,
                emails: updateEmails(prev.emails || [])
            }));
        } else {
            setNewVendorData(prev => ({
                ...prev,
                emails: updateEmails(prev.emails || [])
            }));
        }
    };

    // --- FIX: Define filteredVendors ---
    const filteredVendors = useMemo(() => {
        // Apply client-side filtering based on debouncedSearchTerm AFTER data is fetched
        // Note: Backend search is usually preferred for large datasets
        if (!debouncedSearchTerm) {
            return vendors; // No search term, return all fetched vendors
        }
        const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
        return vendors.filter(vendor =>
            (vendor.name && vendor.name.toLowerCase().includes(lowerSearchTerm)) ||
            (vendor.taxId && vendor.taxId.toLowerCase().includes(lowerSearchTerm))
        );
    }, [vendors, debouncedSearchTerm]); // Re-filter when vendors or search term changes


    // Table columns
    const tableHeaders = ['Name', 'Tax ID', 'TDS Rule', 'Actions'];

    // Map filtered vendors to table data
    const tableData = filteredVendors.map(vendor => [
        vendor.name || 'N/A',
        vendor.taxId || 'N/A',
        vendor.tdsRule?.description || <span className="text-gray-400 italic">Not Set</span>,
        <div key={`${vendor.id}-actions`} className="flex space-x-2">
            <Button
                size="sm"
                variant="outline"
                onClick={() => handleOpenViewModal(vendor)}
            >
                <I.Eye className="w-4 h-4 mr-1" /> View
            </Button>
            {!isAuditor && (
                <>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenEditModal(vendor)}
                    >
                        <I.Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setVendorToDelete(vendor)}
                    >
                        <I.Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                </>
            )}
        </div>
    ]);

    // --- Filter Options ---
    const filterOptions = [
        { value: 'ALL', label: 'All TDS Status' },
        { value: 'SET', label: 'TDS Set' },
        { value: 'NOT_SET', label: 'TDS Not Set' }
    ];

    // --- TDS Rule Options for Select ---
    const tdsRuleOptions = useMemo(() => {
        if (loadingTdsRules) return [{ value: '', label: 'Loading Rules...' }];
        if (!tdsRules || tdsRules.length === 0) return [{ value: '', label: 'No Rules Found' }];
        return [
            { value: '', label: 'Select TDS Rule...' }, // Placeholder
            ...tdsRules.map(rule => ({
                value: rule.id.toString(),
                label: rule.description
            }))
        ];
    }, [tdsRules, loadingTdsRules]);

    // Handle delete confirmation
    const handleDeleteVendor = async () => {
        if (!vendorToDelete) return;

        try {
            const token = localStorage.getItem('token');
            await api.delete(`/vendors/${vendorToDelete.id}`, {
                headers: { 'Authorization': `Bearer ${token}` },
                withCredentials: true
            });

            // Show success toast with vendor name
            showToast(
                <div className="flex items-center gap-2">
                    <I.CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>Vendor <span className="font-semibold">{vendorToDelete.name}</span> deleted successfully</span>
                </div>,
                'success',
                { autoClose: 3000 }
            );

            setVendorToDelete(null); // Close the modal
            fetchVendors(); // Refresh the list
        } catch (err) {
            console.error('Error deleting vendor:', err);

            // Show error toast with vendor name and error details
            showToast(
                <div className="flex items-start gap-2">
                    <I.AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-medium">Failed to delete vendor</p>
                        <p className="text-sm text-gray-600">{err.response?.data?.message || 'Please try again later'}</p>
                    </div>
                </div>,
                'error',
                { autoClose: 5000 }
            );
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <PageHeader
                title="Vendor Management"
                subtitle="Search and manage vendors"
                actions={[
                    <Button key="refresh-vendor" variant="outline" onClick={fetchVendors} disabled={loading}>
                        <I.RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>,
                    ...(!isAuditor ? [
                        <Button key="add-vendor" onClick={handleOpenAddModal}>
                            <I.Plus className="w-4 h-4 mr-2" /> Add Vendor
                        </Button>
                    ] : [])
                ]}
            />

            {/* --- Add Vendor Modal --- */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Vendor">
                <form onSubmit={handleAddFormSubmit} className="space-y-6">
                    {addFormError && (
                        <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-200">
                            {addFormError}
                        </div>
                    )}
                    <div>
                        <label htmlFor="add-name" className="block text-sm font-medium text-gray-700 mb-1">
                            Vendor Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="add-name"
                            name="name"
                            type="text"
                            value={newVendorData.name}
                            onChange={handleAddFormChange}
                            placeholder="e.g., Acme Inc."
                            required
                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="add-taxId" className="block text-sm font-medium text-gray-700 mb-1">
                            Tax ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="add-taxId"
                            name="taxId"
                            type="text"
                            value={newVendorData.taxId}
                            onChange={handleAddFormChange}
                            placeholder="Enter tax ID"
                            required
                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                            style={{ textTransform: 'uppercase' }}
                        />
                    </div>

                    {/* PAN and Bank Details Section */}
                    <div className="border-t border-gray-200 pt-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">PAN & TAN and Bank Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="add-panNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                    PAN Number
                                </label>
                                <input
                                    id="add-panNumber"
                                    name="panNumber"
                                    type="text"
                                    value={newVendorData.panNumber}
                                    onChange={handleAddFormChange}
                                    placeholder="Enter PAN number"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                    style={{ textTransform: 'uppercase' }}
                                    maxLength="10"
                                />
                            </div>
                            <div>
                                <label htmlFor="add-tanNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                    TAN Number
                                </label>
                                <input
                                    id="add-tanNumber"
                                    name="tanNumber"
                                    type="text"
                                    value={newVendorData.tanNumber}
                                    onChange={handleAddFormChange}
                                    placeholder="Enter TAN number"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                    style={{ textTransform: 'uppercase' }}
                                    maxLength="10"
                                />
                            </div>
                            <div>
                                <label htmlFor="add-bankAccountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                    Bank Account Number
                                </label>
                                <input
                                    id="add-bankAccountNumber"
                                    name="bankAccountNumber"
                                    type="text"
                                    value={newVendorData.bankAccountNumber}
                                    onChange={handleAddFormChange}
                                    placeholder="Enter bank account number"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="add-bankName" className="block text-sm font-medium text-gray-700 mb-1">
                                    Bank Name
                                </label>
                                <input
                                    id="add-bankName"
                                    name="bankName"
                                    type="text"
                                    value={newVendorData.bankName}
                                    onChange={handleAddFormChange}
                                    placeholder="Enter bank name"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="add-bankIfscCode" className="block text-sm font-medium text-gray-700 mb-1">
                                    IFSC Code
                                </label>
                                <input
                                    id="add-bankIfscCode"
                                    name="bankIfscCode"
                                    type="text"
                                    value={newVendorData.bankIfscCode}
                                    onChange={handleAddFormChange}
                                    placeholder="Enter IFSC code"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                    style={{ textTransform: 'uppercase' }}
                                    maxLength="11"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label htmlFor="add-bankBranch" className="block text-sm font-medium text-gray-700 mb-1">
                                    Bank Branch
                                </label>
                                <input
                                    id="add-bankBranch"
                                    name="bankBranch"
                                    type="text"
                                    value={newVendorData.bankBranch}
                                    onChange={handleAddFormChange}
                                    placeholder="Enter bank branch"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* --- Email Addresses Section --- */}
                    <div className="border-t border-gray-200 pt-4 mt-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg font-medium text-gray-900">Email Addresses</h3>
                            <Button type="button" size="sm" variant="outline" onClick={() => handleAddEmail(false)}>
                                <I.Plus className="w-4 h-4 mr-1" /> Add Email
                            </Button>
                        </div>
                        {newVendorData.emails && newVendorData.emails.length > 0 ? (
                            <div className="space-y-3">
                                {newVendorData.emails.map((email, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <input
                                            type="email"
                                            placeholder="vendor@example.com"
                                            value={email.email}
                                            onChange={(e) => handleEmailChange(index, 'email', e.target.value, false)}
                                            className="flex-1 p-2 h-9 border border-gray-300 rounded-md text-sm"
                                        />
                                        <label className="flex items-center space-x-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={email.default}
                                                onChange={(e) => handleEmailChange(index, 'default', e.target.checked, false)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span>Default</span>
                                        </label>
                                        <label className="flex items-center space-x-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={email.cc || false}
                                                onChange={(e) => handleEmailChange(index, 'cc', e.target.checked, false)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span>CC</span>
                                        </label>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => handleRemoveEmail(index, false)}
                                            className="h-9 w-9 p-0"
                                        >
                                            <I.Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 italic">No email addresses added.</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="add-tdsRuleId" className="block text-sm font-medium text-gray-700 mb-1">
                            TDS Rule <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="flex gap-2">
                                <select
                                    id="add-tdsRuleId"
                                    name="tdsRuleId"
                                    value={newVendorData.tdsRuleId || ''}
                                    onChange={handleAddFormChange}
                                    className="flex-1 p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white"
                                    disabled={loadingTdsRules}
                                    required
                                >
                                    <option value="">Select a TDS Rule</option>
                                    {tdsRules.map(rule => (
                                        <option key={rule.id} value={rule.id}>
                                            {rule.description}
                                        </option>
                                    ))}
                                </select>
                                <Button 
                                    type="button" 
                                    size="sm" 
                                    variant="outline" 
                                    className="h-10 w-10 p-0 flex-shrink-0"
                                    onClick={() => setIsAddTdsRuleModalOpen(true)}
                                    title="Create New TDS Rule"
                                >
                                    <I.Plus className="w-5 h-5" />
                                </Button>
                            </div>
                            {loadingTdsRules && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                    <LoadingSpinner size="sm" />
                                </div>
                            )}
                        </div>
                        {!loadingTdsRules && tdsRules.length === 0 && (
                            <p className="mt-1 text-sm text-red-600">No TDS rules available. Please contact support.</p>
                        )}
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsAddModalOpen(false)}
                            disabled={isSubmitting}
                            className="flex items-center"
                        >
                            <I.ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || loadingTdsRules}
                            className="flex items-center"
                        >
                            {isSubmitting ? (
                                <LoadingSpinner size="sm" className="mr-2" />
                            ) : (
                                <I.Save className="w-4 h-4 mr-2" />
                            )}
                            {isSubmitting ? 'Saving...' : 'Save Vendor'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* --- Delete Confirmation Modal --- */}
            <Modal
                isOpen={!!vendorToDelete}
                onClose={() => !loading && setVendorToDelete(null)}
                title={
                    <div className="flex items-center gap-2 text-red-600">
                        <I.AlertTriangle className="w-5 h-5" />
                        <span>Confirm Vendor Deletion</span>
                    </div>
                }
                size="md"
            >
                <div className="space-y-6">
                    <div className="bg-red-50 p-4 rounded-md border border-red-100">
                        <p className="text-gray-700">
                            Are you sure you want to delete vendor <span className="font-semibold">{vendorToDelete?.name}</span>? This will remove all associated invoices and payment records.
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                            <I.AlertCircle className="inline w-4 h-4 mr-1 -mt-0.5" />
                            All invoice history and payment data for this vendor will be permanently removed.
                        </p>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setVendorToDelete(null)}
                            disabled={loading}
                            className="flex items-center gap-2"
                        >
                            <I.X className="w-4 h-4" />
                            <span>Cancel</span>
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleDeleteVendor}
                            disabled={loading}
                            className="flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <I.Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Deleting...</span>
                                </>
                            ) : (
                                <>
                                    <I.Trash2 className="w-4 h-4" />
                                    <span>Delete Vendor</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* --- View Vendor Modal --- */}
            <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Vendor Details">
                {viewingVendor && (
                    <div className="space-y-6">
                        {/* Basic Information */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <I.Building className="w-5 h-5 mr-2 text-blue-500" />
                                Basic Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Vendor Name</label>
                                    <p className="text-gray-900 font-medium">{viewingVendor.name || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Tax ID</label>
                                    <p className="text-gray-900 font-mono">{viewingVendor.taxId || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">TDS Rule</label>
                                    <p className="text-gray-900">
                                        {viewingVendor.tdsRule?.description || <span className="text-gray-400 italic">Not Set</span>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* PAN & TAN Information */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <I.FileText className="w-5 h-5 mr-2 text-green-500" />
                                PAN & TAN Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">PAN Number</label>
                                    <p className="text-gray-900 font-mono">
                                        {viewingVendor.panNumber || <span className="text-gray-400 italic">Not Provided</span>}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">TAN Number</label>
                                    <p className="text-gray-900 font-mono">
                                        {viewingVendor.tanNumber || <span className="text-gray-400 italic">Not Provided</span>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Bank Details */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <I.CreditCard className="w-5 h-5 mr-2 text-purple-500" />
                                Bank Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Bank Account Number</label>
                                    <p className="text-gray-900 font-mono">
                                        {viewingVendor.bankAccountNumber || <span className="text-gray-400 italic">Not Provided</span>}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Bank Name</label>
                                    <p className="text-gray-900">
                                        {viewingVendor.bankName || <span className="text-gray-400 italic">Not Provided</span>}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">IFSC Code</label>
                                    <p className="text-gray-900 font-mono">
                                        {viewingVendor.bankIfscCode || <span className="text-gray-400 italic">Not Provided</span>}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Bank Branch</label>
                                    <p className="text-gray-900">
                                        {viewingVendor.bankBranch || <span className="text-gray-400 italic">Not Provided</span>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Email Addresses */}
                        <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <I.Mail className="w-5 h-5 mr-2 text-orange-500" />
                                Email Addresses
                            </h3>
                            {viewingVendor.emails && viewingVendor.emails.length > 0 ? (
                                <div className="grid grid-cols-1 gap-3">
                                    {viewingVendor.emails.map((email, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-100">
                                            <div className="flex items-center">
                                                <I.Mail className="w-4 h-4 text-gray-400 mr-2" />
                                                <span className="text-gray-900 font-medium">{email.email}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {email.default && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        Default
                                                    </span>
                                                )}
                                                {email.cc && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                        CC
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No email addresses found.</p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <Button
                                variant="outline"
                                onClick={() => setIsViewModalOpen(false)}
                                className="flex items-center"
                            >
                                <I.X className="w-4 h-4 mr-2" />
                                Close
                            </Button>
                            {!isAuditor && (
                                <Button
                                    onClick={() => {
                                        setIsViewModalOpen(false);
                                        handleOpenEditModal(viewingVendor);
                                    }}
                                    className="flex items-center"
                                >
                                    <I.Edit className="w-4 h-4 mr-2" />
                                    Edit Vendor
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* --- Edit Vendor Modal --- */}
            <Modal isOpen={isEditModalOpen} onClose={() => !isUpdating && setIsEditModalOpen(false)} title="Edit Vendor">
                {editingVendor && (
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                        {editFormError && (
                            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-200">
                                {editFormError}
                            </div>
                        )}

                        <div className="space-y-3">
                            <div>
                                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                                    Vendor Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="edit-name"
                                    name="name"
                                    type="text"
                                    value={editingVendor.name}
                                    onChange={handleEditFormChange}
                                    placeholder="e.g., Acme Inc."
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    required
                                />
                            </div>

                            {/* PAN and Bank Details Section */}
                            <div className="border-t border-gray-200 pt-4 mt-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">PAN & TAN and Bank Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="edit-panNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                            PAN Number
                                        </label>
                                        <input
                                            id="edit-panNumber"
                                            name="panNumber"
                                            type="text"
                                            value={editingVendor.panNumber || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="Enter PAN number"
                                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                            style={{ textTransform: 'uppercase' }}
                                            maxLength="10"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-tanNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                            TAN Number
                                        </label>
                                        <input
                                            id="edit-tanNumber"
                                            name="tanNumber"
                                            type="text"
                                            value={editingVendor.tanNumber || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="Enter TAN number"
                                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                            style={{ textTransform: 'uppercase' }}
                                            maxLength="10"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-bankAccountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                            Bank Account Number
                                        </label>
                                        <input
                                            id="edit-bankAccountNumber"
                                            name="bankAccountNumber"
                                            type="text"
                                            value={editingVendor.bankAccountNumber || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="Enter bank account number"
                                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-bankName" className="block text-sm font-medium text-gray-700 mb-1">
                                            Bank Name
                                        </label>
                                        <input
                                            id="edit-bankName"
                                            name="bankName"
                                            type="text"
                                            value={editingVendor.bankName || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="Enter bank name"
                                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-bankIfscCode" className="block text-sm font-medium text-gray-700 mb-1">
                                            IFSC Code
                                        </label>
                                        <input
                                            id="edit-bankIfscCode"
                                            name="bankIfscCode"
                                            type="text"
                                            value={editingVendor.bankIfscCode || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="Enter IFSC code"
                                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                            style={{ textTransform: 'uppercase' }}
                                            maxLength="11"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label htmlFor="edit-bankBranch" className="block text-sm font-medium text-gray-700 mb-1">
                                            Bank Branch
                                        </label>
                                        <input
                                            id="edit-bankBranch"
                                            name="bankBranch"
                                            type="text"
                                            value={editingVendor.bankBranch || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="Enter bank branch"
                                            className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="edit-taxId" className="block text-sm font-medium text-gray-700 mb-1">
                                    Tax ID (GSTIN) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="edit-taxId"
                                    name="taxId"
                                    type="text"
                                    value={editingVendor.taxId}
                                    onChange={handleEditFormChange}
                                    placeholder="Enter tax ID"
                                    className="w-full p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm uppercase"
                                    style={{ textTransform: 'uppercase', minWidth: '300px' }}
                                    required
                                />
                            </div>

                            <div className="w-full">
                                <label htmlFor="edit-tdsRuleId" className="block text-sm font-medium text-gray-700 mb-1">
                                    TDS Rule <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    {loadingTdsRules ? (
                                        <div className="p-3 bg-gray-100 rounded-md text-sm text-gray-500 w-full">
                                            Loading TDS rules...
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex gap-2">
                                                <select
                                                    id="edit-tdsRuleId"
                                                    name="tdsRuleId"
                                                    value={editingVendor.tdsRuleId || ''}
                                                    onChange={handleEditFormChange}
                                                    className="flex-1 p-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white"
                                                    required
                                                >
                                                    <option value="">Select a TDS Rule</option>
                                                    {tdsRules.map(rule => (
                                                        <option key={rule.id} value={rule.id}>
                                                            {rule.description}
                                                        </option>
                                                    ))}
                                                </select>
                                                <Button 
                                                    type="button" 
                                                    size="sm" 
                                                    variant="outline" 
                                                    className="h-10 w-10 p-0 flex-shrink-0"
                                                    onClick={() => setIsAddTdsRuleModalOpen(true)}
                                                    title="Create New TDS Rule"
                                                >
                                                    <I.Plus className="w-5 h-5" />
                                                </Button>
                                            </div>
                                            <div className="mt-1 text-xs text-gray-500">
                                                Current selection: {editingVendor.tdsRuleId || 'None'}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {!loadingTdsRules && tdsRules.length === 0 && (
                                    <p className="mt-1 text-sm text-red-600">No TDS rules available. Please contact support.</p>
                                )}
                            </div>
                        </div>

                        {/* --- Email Addresses Section --- */}
                        <div className="border-t border-gray-200 pt-4 mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-medium text-gray-900">Email Addresses</h3>
                                <Button type="button" size="sm" variant="outline" onClick={() => handleAddEmail(true)}>
                                    <I.Plus className="w-4 h-4 mr-1" /> Add Email
                                </Button>
                            </div>
                            {editingVendor.emails && editingVendor.emails.length > 0 ? (
                                <div className="space-y-3">
                                    {editingVendor.emails.map((email, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <input
                                                type="email"
                                                placeholder="vendor@example.com"
                                                value={email.email || ''}
                                                onChange={(e) => handleEmailChange(index, 'email', e.target.value, true)}
                                                className="flex-1 p-2 h-9 border border-gray-300 rounded-md text-sm"
                                            />
                                            <label className="flex items-center space-x-2 text-sm text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={email.default || false}
                                                    onChange={(e) => handleEmailChange(index, 'default', e.target.checked, true)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span>Default</span>
                                            </label>
                                            <label className="flex items-center space-x-2 text-sm text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={email.cc || false}
                                                    onChange={(e) => handleEmailChange(index, 'cc', e.target.checked, true)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span>CC</span>
                                            </label>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => handleRemoveEmail(index, true)}
                                                className="h-9 w-9 p-0"
                                            >
                                                <I.Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No email addresses added.</p>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsEditModalOpen(false)}
                                disabled={isUpdating}
                                className="flex items-center"
                            >
                                <I.X className="w-4 h-4 mr-2" />
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isUpdating || loadingTdsRules}
                                className="flex items-center"
                            >
                                {isUpdating ? (
                                    <LoadingSpinner size="sm" className="mr-2" />
                                ) : (
                                    <I.Save className="w-4 h-4 mr-2" />
                                )}
                                {isUpdating ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </form>
                )}
            </Modal>


            <Card className="mt-6 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 border-b border-gray-200">
                    <div className="flex-1 min-w-0 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <I.Search className="w-4 h-4 text-gray-400" />
                        </span>
                        <input
                            type="text"
                            placeholder="Search by Vendor Name or Tax ID..."
                            value={searchTerm}
                            onChange={handleSearchChange} // Use the handler that debounces
                            className="w-full pl-10 pr-10 py-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {searchTerm && (
                            <button type="button" onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600" title="Clear search">
                                <I.X className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0 w-full lg:w-auto">
                        <div className="w-full lg:w-auto lg:min-w-[200px]">
                            <Select
                                id="statusFilter" value={filter} onChange={handleFilterChange}
                                leftIcon={<I.Filter className="w-4 h-4 text-gray-400" />}
                                options={filterOptions}
                            />
                        </div>
                        {(searchTerm || filter !== 'ALL') && (
                            <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); setFilter('ALL'); setCurrentPage(0); }} title="Clear search and filter">
                                <I.X className="w-4 h-4 mr-1" /> Clear
                            </Button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading && vendors.length === 0 ? (
                        <div className="flex justify-center py-16"><LoadingSpinner size="lg" /> </div>
                    ) : error ? (
                        <div className="text-center py-16 px-4">
                            <div className="text-red-500 mb-4"><I.AlertTriangle className="w-12 h-12 mx-auto mb-3" /></div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Error Loading Vendors</h3>
                            <p className="text-gray-500 text-sm mb-4">{error}</p>
                            <Button onClick={fetchVendors} variant="outline">Try Again</Button>
                        </div>
                    ) : filteredVendors.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <I.SearchX className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Vendors Found</h3>
                            <p className="text-gray-500 text-sm mb-4">
                                {debouncedSearchTerm || filter !== 'ALL'
                                    ? "No vendors match your current search or filter criteria."
                                    : "Get started by adding your first vendor."}
                            </p>
                            {/* Optionally show Add button here too if vendors array is also empty */}
                            {!isAuditor && !debouncedSearchTerm && filter === 'ALL' && vendors.length === 0 && (
                                <Button onClick={handleOpenAddModal}> <I.Plus className="w-4 h-4 mr-2" /> Add Vendor </Button>
                            )}
                        </div>
                    ) : (
                        // --- Replaced <table> with <Table /> component ---
                        <Table
                            headers={tableHeaders}
                            data={tableData}
                        />
                    )}
                </div>


                {totalPages > 1 && !error && filteredVendors.length > 0 && ( // Show pagination only if needed, no error, and filtered results exist
                    // --- Standardized padding to p-4 ---
                    <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-2">
                        <div className="text-xs sm:text-sm text-gray-500">
                            Showing <span className="font-medium">{(currentPage * pageSize) + 1}</span>-
                            <span className="font-medium">{Math.min((currentPage + 1) * pageSize, totalVendors)}</span> of{' '}
                            <span className="font-medium">{totalVendors}</span> results (filtered)
                        </div>
                        <Pagination
                            currentPage={currentPage + 1} // Convert 0-based to 1-based for Pagination component
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </Card>
            {/* --- Add TDS Rule Sub-Modal --- */}
            <Modal 
                isOpen={isAddTdsRuleModalOpen} 
                onClose={() => !isCreatingTdsRule && setIsAddTdsRuleModalOpen(false)} 
                title="Create New TDS Rule"
                size="md"
            >
                <form onSubmit={handleCreateTdsRule} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rule Code (e.g., 94C, 94J) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={newTdsRuleData.ruleCode}
                            onChange={(e) => setNewTdsRuleData(prev => ({ ...prev, ruleCode: e.target.value.toUpperCase() }))}
                            placeholder="94C"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm uppercase"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={newTdsRuleData.description}
                            onChange={(e) => setNewTdsRuleData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Contractors (2%)"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Percentage <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={newTdsRuleData.percentage}
                            onChange={(e) => setNewTdsRuleData(prev => ({ ...prev, percentage: e.target.value }))}
                            placeholder="2.0"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            required
                        />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsAddTdsRuleModalOpen(false)}
                            disabled={isCreatingTdsRule}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isCreatingTdsRule}
                        >
                            {isCreatingTdsRule ? 'Creating...' : 'Create Rule'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default VendorSearchPage;